import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import i18n, { SUPPORTED_LANGUAGES, loadLanguage, type SupportedLanguage } from "@/i18n";

export type AppRole = "owner" | "admin" | "estimate" | "supervisor" | "employee" | "client";

async function applyUserLanguage(lang: string | null | undefined) {
  if (!lang) return;
  const normalized = lang.split("-")[0] as SupportedLanguage;
  if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(normalized)) return;
  try {
    await loadLanguage(normalized);
    if (i18n.resolvedLanguage !== normalized) {
      await i18n.changeLanguage(normalized);
    }
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem("app.lang", normalized);
      } catch {
        // ignore storage errors (private mode)
      }
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = normalized;
    }
  } catch {
    // ignore — fallback language remains active
  }
}

export interface CompanyInfo {
  id: string;
  name: string;
  logo_url: string | null;
  timezone: string;
  currency: string;
  plan: string;
  tax_percent: number;
  owner_name: string | null;
  gps_default_radius_meters: number;
  require_job_site_gps: boolean;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  trial_ends_at: string | null;
  plan_override: "starter" | "pro" | "business" | "enterprise" | null;
}

export interface ProfileInfo {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
}

/** A company the current user belongs to (has at least one role in). */
export interface CompanyOption {
  id: string;
  name: string;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: ProfileInfo | null;
  company: CompanyInfo | null;
  roles: AppRole[];
  /** Companies the user belongs to (for the multi-company switcher). */
  companies: CompanyOption[];
  /** The currently active company id (what all RLS/queries resolve to). */
  activeCompanyId: string | null;
  /** The owner's home company id (profiles.company_id) — used to detect/exit support mode. */
  homeCompanyId: string | null;
  /** Switch the active company (validates membership server-side, then reloads). */
  setActiveCompany: (companyId: string) => Promise<void>;
  loading: boolean;
  hasRole: (r: AppRole) => boolean;
  hasAnyRole: (rs: AppRole[]) => boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ProfileInfo | null>(null);
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);
  const [homeCompanyId, setHomeCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadContext = async (uid: string) => {
    const { data: prof } = await supabase
      .from("profiles")
      .select("id, full_name, email, avatar_url, company_id, active_company_id, language")
      .eq("id", uid)
      .maybeSingle();

    if (!prof) {
      setProfile(null);
      setCompany(null);
      setRoles([]);
      setCompanies([]);
      setActiveCompanyId(null);
      setHomeCompanyId(null);
      return;
    }

    setProfile({
      id: prof.id,
      full_name: prof.full_name,
      email: prof.email,
      avatar_url: prof.avatar_url,
    });
    void applyUserLanguage((prof as { language?: string | null }).language);

    // Active company: prefer the chosen active company, fall back to the home company.
    const activeId =
      (prof as { active_company_id?: string | null }).active_company_id ?? prof.company_id;
    setActiveCompanyId(activeId);
    setHomeCompanyId(prof.company_id);

    // Load the ACTIVE company record + all role rows (with company names for the switcher).
    const [{ data: comp }, { data: allRoles }] = await Promise.all([
      supabase
        .from("companies")
        .select(
          "id, name, logo_url, timezone, currency, plan, tax_percent, owner_name, gps_default_radius_meters, require_job_site_gps, address_line1, address_line2, city, state, postal_code, country, phone, email, website, trial_ends_at, plan_override",
        )
        .eq("id", activeId)
        .maybeSingle(),
      supabase
        .from("user_roles")
        .select("role, company_id, companies(id, name)")
        .eq("user_id", uid),
    ]);
    if (comp) setCompany(comp as CompanyInfo);

    const rows = (allRoles ?? []) as Array<{
      role: AppRole;
      company_id: string;
      companies: { id: string; name: string } | { id: string; name: string }[] | null;
    }>;

    // Roles are scoped to the ACTIVE company. `owner` is platform-wide, so it's
    // included whenever the user is an owner in any company.
    const activeRoles = rows.filter((r) => r.company_id === activeId).map((r) => r.role);
    const isOwner = rows.some((r) => r.role === "owner");
    setRoles(isOwner && !activeRoles.includes("owner") ? [...activeRoles, "owner"] : activeRoles);

    // Membership list (distinct companies) for the switcher.
    const map = new Map<string, CompanyOption>();
    for (const r of rows) {
      const c = Array.isArray(r.companies) ? r.companies[0] : r.companies;
      if (c?.id) map.set(c.id, { id: c.id, name: c.name });
    }
    setCompanies([...map.values()].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const setActiveCompany = async (companyId: string) => {
    const { error } = await supabase.rpc("set_active_company", { _company_id: companyId });
    if (error) throw error;
    // Full reload so every route re-scopes its data to the new active company.
    if (typeof window !== "undefined") window.location.reload();
  };

  useEffect(() => {
    let loadedForUserId: string | null = null;
    let cancelled = false;

    const handleSession = (newSession: Session | null) => {
      if (cancelled) return;
      setSession(newSession);
      setUser(newSession?.user ?? null);
      const uid = newSession?.user?.id ?? null;

      if (uid) {
        // Skip reloading on TOKEN_REFRESHED / repeated INITIAL_SESSION for the same user.
        // IMPORTANT: do NOT flip loading=false here — the first loadContext may still be
        // in flight and roles/profile not yet populated. Flipping early causes gated
        // routes to redirect as if the user had no role (e.g. owner -> /upgrade -> /dashboard loop).
        if (loadedForUserId === uid) {
          return;
        }
        loadedForUserId = uid;
        // defer to avoid auth-state deadlock
        setTimeout(() => {
          if (cancelled) return;
          loadContext(uid).finally(() => {
            if (!cancelled) setLoading(false);
          });
        }, 0);
      } else {
        loadedForUserId = null;
        setProfile(null);
        setCompany(null);
        setRoles([]);
        setCompanies([]);
        setActiveCompanyId(null);
        setHomeCompanyId(null);
        setLoading(false);
      }
    };

    // Register listener FIRST so we don't miss any auth event.
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      handleSession(newSession);
    });

    // THEN explicitly read the persisted session. We can't rely on
    // INITIAL_SESSION alone — in some browsers/SSR scenarios it never fires,
    // which would leave the app stuck on the loading spinner forever.
    // The `loadedForUserId` guard above prevents duplicate loadContext calls.
    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      handleSession(existing);
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const refresh = async () => {
    if (user?.id) await loadContext(user.id);
  };

  const value: AuthContextValue = {
    session,
    user,
    profile,
    company,
    roles,
    companies,
    activeCompanyId,
    homeCompanyId,
    setActiveCompany,
    loading,
    // Owner tem acesso TOTAL: satisfaz qualquer checagem de papel. Admin permanece exato.
    hasRole: (r) => roles.includes("owner") || roles.includes(r),
    hasAnyRole: (rs) => roles.includes("owner") || rs.some((r) => roles.includes(r)),
    signOut,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
